/**
 * 播放结束与下一首判断（纯函数，无 React 依赖）
 * 供 MusicPlayer handleEnded 使用，便于测试与梳理。
 */

/**
 * 播放结束后的动作类型
 * - replay_same: 同首重播（当前 media 置 0 并 play）
 * - switch_next: 切到下一首（调用 handleNext + 延迟 updatePlaying）
 * - stop: 停止并 setShowLyrics(false)
 */
export const ENDED_ACTION_REPLAY_SAME = 'replay_same';
export const ENDED_ACTION_SWITCH_NEXT = 'switch_next';
export const ENDED_ACTION_STOP = 'stop';

/**
 * 根据播放模式与播放列表判断“下一首”信息。
 * @param {string} playMode - 'sequential' | 'random' | 'repeat'
 * @param {boolean} playlistLoop - 播放列表是否循环
 * @param {Array} playlist - 播放列表
 * @param {number} currentIndex - 当前曲目在 playlist 中的索引
 * @returns {{ hasNext: boolean, nextIndex: number | null, nextMusic: object | null }}
 */
export function getNextTrackAfterEnd(playMode, playlistLoop, playlist, currentIndex) {
  if (!playlist || playlist.length === 0) {
    return { hasNext: false, nextIndex: null, nextMusic: null };
  }

  if (playMode === 'random') {
    // random：具体下一首由播放器的 handleNext 决定（支持“最近 N 首去重 + 一轮不重复”）
    // 这里只负责告诉 endedHandler “可以继续播”，不在此做随机抽样，避免与 handleNext 的策略冲突。
    return { hasNext: true, nextIndex: null, nextMusic: null };
  }

  if (playMode === 'repeat') {
    const idx = currentIndex >= 0 && currentIndex < playlist.length ? currentIndex : 0;
    return { hasNext: true, nextIndex: idx, nextMusic: playlist[idx] || null };
  }

  // sequential
  if (playlistLoop) {
    const nextIndex = currentIndex < playlist.length - 1 ? currentIndex + 1 : 0;
    return { hasNext: true, nextIndex, nextMusic: playlist[nextIndex] || null };
  }
  const hasNext = currentIndex >= 0 && currentIndex < playlist.length - 1;
  const nextIndex = hasNext ? currentIndex + 1 : null;
  const nextMusic = nextIndex !== null ? playlist[nextIndex] : null;
  return { hasNext, nextIndex, nextMusic };
}

/**
 * 根据播放模式、播放列表与 getNextTrackAfterEnd 结果决定结束后的动作。
 * @param {string} playMode - 'sequential' | 'random' | 'repeat'
 * @param {Array} playlist - 播放列表
 * @param {object} currentMusic - 当前曲目
 * @param {{ hasNext: boolean, nextIndex: number | null, nextMusic: object | null }} nextResult - getNextTrackAfterEnd 的返回值
 * @returns {string} ENDED_ACTION_REPLAY_SAME | ENDED_ACTION_SWITCH_NEXT | ENDED_ACTION_STOP
 */
export function getEndedAction(playMode, playlist, currentMusic, nextResult) {
  if (playMode === 'repeat') {
    return ENDED_ACTION_REPLAY_SAME;
  }
  if (!playlist || playlist.length === 0) {
    return ENDED_ACTION_STOP;
  }
  if (playMode === 'random') {
    // random：单曲列表只能重播；多曲列表一律切到下一首（由 handleNext 负责挑选）
    return playlist.length <= 1 ? ENDED_ACTION_REPLAY_SAME : ENDED_ACTION_SWITCH_NEXT;
  }
  const { hasNext, nextMusic } = nextResult;
  if (!hasNext) {
    return ENDED_ACTION_STOP;
  }
  if (nextMusic && nextMusic.id === currentMusic?.id) {
    return ENDED_ACTION_REPLAY_SAME;
  }
  return ENDED_ACTION_SWITCH_NEXT;
}
